import React from "react";
import { useBranding } from "@/context/BrandingContext";

const FADE_MS = 400;

export function PageLoader({ dark = true, phase = "visible" }: { dark?: boolean; phase?: "in" | "out" | "visible" }) {
  const { branding, state } = useBranding();
  // Per-partner branding, but only ONCE resolved. During the pre-resolve window
  // (e.g. a page reload) use the neutral-friendly cyan accent — never the grey
  // loading-placeholder colour — and no wordmark on a partner host (its name
  // isn't known yet), so a reload never flashes the wrong name.
  const ready = state === "ready";
  const wordmark = branding.shortName ?? (ready ? branding.productName : "");
  const id = React.useId();
  const bg = dark ? "#101010" : "#FAFAFA";
  // The original Verity shimmer (background-clip:text, 6s). Verity keeps prod's
  // EXACT accent — cyan swept mid, blue peak in light mode; only a REAL partner
  // (a resolved primaryColor that isn't the Verity brand blue) swaps in their own
  // colour. During load, or for the Verity default, the accent stays cyan.
  const VERITY_BLUE = "#3455d3";
  const partnerColor =
    ready &&
    branding.primaryColor &&
    branding.primaryColor.trim().toLowerCase() !== VERITY_BLUE
      ? branding.primaryColor
      : null;
  const mid = partnerColor ?? "#60a5fa";
  const baseColor = dark ? "#555" : "#888";
  const peakColor = dark ? "#fff" : (partnerColor ?? "#3455D3");
  const subBase = dark ? "#444" : "#aaa";
  const subPeak = dark ? "#999" : "#555";

  const opacity = phase === "out" ? 0 : phase === "in" ? 0 : 1;
  const scale = phase === "in" ? 0.97 : phase === "out" ? 1.02 : 1;

  return (
    <div style={{
      position: "fixed", inset: 0, background: bg,
      display: "flex", flexDirection: "column", alignItems: "center",
      gap: 14, zIndex: 9999,
      opacity,
      transition: `opacity ${FADE_MS}ms ease`,
    }}>
      <div style={{ flex: "1 1 35%" }} />
      <style>{`
        @keyframes pl-shimmer-${id} {
          0%   { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
      `}</style>
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
        transform: `scale(${scale})`,
        transition: `transform ${FADE_MS}ms ease`,
      }}>
        {wordmark && (
        <span style={{
          fontFamily: "Google Sans Flex, Google Sans, sans-serif",
          fontSize: 56,
          fontWeight: 500,
          letterSpacing: "0.05em",
          // longhand `backgroundImage`, NOT the `background` shorthand — the
          // shorthand resets `background-clip` to border-box, so a gradient
          // update on the branding re-render would un-clip the text and leak the
          // grey rectangle. The longhand leaves background-clip:text intact.
          backgroundImage: `linear-gradient(90deg,${baseColor} 0%, ${baseColor} 35%, ${mid} 48%, ${peakColor} 50%, ${mid} 52%, ${baseColor} 65%, ${baseColor} 100%)`,
          backgroundSize: "200% 100%",
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          color: "transparent",
          WebkitTextFillColor: "transparent",
          animation: `pl-shimmer-${id} 6s ease-in-out infinite`,
        }}>
          {wordmark}
        </span>
        )}
        <span style={{
          fontFamily: "Google Sans Flex, Google Sans, sans-serif",
          fontSize: 18,
          fontWeight: 400,
          letterSpacing: "0.12em",
          textTransform: "uppercase" as const,
          // longhand `backgroundImage`, NOT the `background` shorthand — the
          // shorthand resets `background-clip` to border-box, so a gradient
          // update on the branding re-render would un-clip the text and leak the
          // grey rectangle. The longhand leaves background-clip:text intact.
          backgroundImage: `linear-gradient(90deg,${subBase} 0%, ${subBase} 35%, ${mid} 48%, ${subPeak} 50%, ${mid} 52%, ${subBase} 65%, ${subBase} 100%)`,
          backgroundSize: "200% 100%",
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          color: "transparent",
          WebkitTextFillColor: "transparent",
          animation: `pl-shimmer-${id} 6s ease-in-out 0.3s infinite`,
        }}>
          loading
        </span>
      </div>
      <div style={{ flex: "1 1 65%" }} />
    </div>
  );
}

/**
 * Suspense wrapper with min-duration PageLoader and fade transitions.
 * Only shows the loader overlay when Suspense is actually suspended.
 */
export function MinDurationSuspense({
  children,
  minMs = 1000,
  dark = true,
}: {
  children: React.ReactNode;
  minMs?: number;
  dark?: boolean;
}) {
  const [suspended, setSuspended] = React.useState(false);
  const mountedAt = React.useRef(0);

  const fallback = React.useMemo(() => (
    <SuspenseDetector onSuspend={() => { mountedAt.current = Date.now(); setSuspended(true); }}>
      <PageLoader dark={dark} />
    </SuspenseDetector>
  ), [dark]);

  return (
    <React.Suspense fallback={fallback}>
      {children}
      {suspended && (
        <FadeOutLoader
          dark={dark}
          mountedAt={mountedAt.current}
          minMs={minMs}
          onDone={() => setSuspended(false)}
        />
      )}
    </React.Suspense>
  );
}

/** Fires onSuspend when mounted (= when Suspense shows the fallback) */
function SuspenseDetector({ onSuspend, children }: { onSuspend: () => void; children: React.ReactNode }) {
  React.useEffect(() => { onSuspend(); }, [onSuspend]);
  return <>{children}</>;
}

/** Waits for minMs since mount, then fades out and removes itself */
function FadeOutLoader({
  dark,
  mountedAt,
  minMs,
  onDone,
}: {
  dark: boolean;
  mountedAt: number;
  minMs: number;
  onDone: () => void;
}) {
  const [phase, setPhase] = React.useState<"visible" | "out">("visible");

  React.useEffect(() => {
    const elapsed = Date.now() - mountedAt;
    const remaining = Math.max(0, minMs - elapsed);

    const waitTimer = setTimeout(() => {
      setPhase("out");
      setTimeout(onDone, FADE_MS);
    }, remaining);

    return () => clearTimeout(waitTimer);
  }, [mountedAt, minMs, onDone]);

  return <PageLoader dark={dark} phase={phase} />;
}
